import { fetch_requestor_application_journals } from '@/services/requestor_portal_service';
import { fetch_case_owner_cases } from '@/views/x-verdict_views/CaseOwner/AllCases/owner_case_queue_service';
import { read_session_project_id } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import {
  journal_activity,
  journal_date,
  journal_hours,
  journal_is_billable,
  journal_user_name,
} from '@/views/x-verdict_views/CompanyAdmin/TimeSheets/time_sheets_formatters';
import {
  normalize_text,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';

const OWNER_DASHBOARD_VIEWS = {
  OwnerDashboard: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Good morning, {name}.',
      summary_segments: [
        { text: 'Your live queue, SLA alerts, and weekly time update below.' }
      ]
    },
    metrics: [
      { label: 'Decisions due today', value: '', detail: 'High-risk - open them first' },
      { label: 'SLA warning', value: '', detail: '< 48hr to breach' },
      { label: 'Awaiting evidence', value: '', detail: 'Claimant requested' },
      { label: 'Hours this week', value: '', detail: 'Target 35h' }
    ],
    queue_title: "Today's priority queue",
    queue_subtitle: 'Ordered by SLA risk x case value'
  },
  OwnerQueue: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Your case queue, {name}.',
      summary_segments: [
        { text: 'Your assigned queue updates from live case data.' }
      ]
    },
    metrics: [
      { label: 'Open cases', value: '', detail: 'Across all routed categories' },
      { label: 'In review', value: '', detail: 'Ready for next decision' },
      { label: 'Pending evidence', value: '', detail: 'External party requested' },
      { label: 'SLA watch', value: '', detail: 'Needs attention this week' }
    ],
    queue_title: 'Queue ordered for action',
    queue_subtitle: 'High urgency first, then evidence and note-driven work'
  },
  OwnerEscalations: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Escalations first.',
      summary_segments: [
        { text: 'Escalations are ordered from live SLA and risk signals.' }
      ]
    },
    metrics: [
      { label: 'Decision overdue', value: '', detail: 'Escalate or resolve now' },
      { label: 'Inspector report', value: '', detail: 'Field findings uploaded' },
      { label: 'High risk cases', value: '', detail: 'Risk score above 70' },
      { label: 'Potential leakage', value: '', detail: 'Across active escalations' }
    ],
    queue_title: 'Escalation queue',
    queue_subtitle: 'Ordered by exposure, policy breach and time since last action'
  },
  OwnerApprovals: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Approvals waiting on you.',
      summary_segments: [
        { text: 'Approval actions update from your live assigned work.' }
      ]
    },
    metrics: [
      { label: 'Approvals due', value: '', detail: 'Owner sign-off required' },
      { label: 'Policy overrides', value: '', detail: 'Exception flow triggered' },
      { label: 'Closures pending', value: '', detail: 'Final decision still open' },
      { label: 'Avg turnaround', value: '', detail: 'Down from 14h' }
    ],
    queue_title: 'Approval queue',
    queue_subtitle: 'Ordered by decision urgency and downstream blocker count'
  },
  OwnerCalendar: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Your day at a glance.',
      summary_segments: [
        { text: 'Your calendar and due items appear here when live data is available.' }
      ]
    },
    metrics: [
      { label: 'Meetings today', value: '', detail: '2 stakeholder reviews' },
      { label: 'Due in 24h', value: '', detail: 'Across all active cases' },
      { label: 'Handoffs', value: '', detail: 'Reassignment needed' },
      { label: 'Free capacity', value: '', detail: 'Tight but manageable' }
    ],
    queue_title: 'Upcoming commitments',
    queue_subtitle: 'Sorted by due time, external dependency and impact'
  },
  OwnerReports: {
    greeting: {
      day_label: 'TUESDAY',
      date_label: '14 MAY 2026',
      title: 'Reporting snapshot.',
      summary_segments: [
        { text: 'Reporting figures update from live operational data.' }
      ]
    },
    metrics: [
      { label: 'Resolved this week', value: '', detail: '+6 vs last week' },
      { label: 'SLA compliance', value: '', detail: 'Stable upward trend' },
      { label: 'Leakage prevented', value: '', detail: 'Month to date' },
      { label: 'QA flags', value: '', detail: 'Below threshold' }
    ],
    queue_title: 'Operational highlights',
    queue_subtitle: 'The biggest decision points driving current reporting'
  }
};

const OWNER_DASHBOARD_ALIASES = {
  OwnerInbox: 'OwnerApprovals',
  OwnerMessages: 'OwnerReports',
  OwnerTimeEntry: 'OwnerCalendar',
  OwnerDecisionsDue: 'OwnerEscalations',
  OwnerInReview: 'OwnerQueue',
  OwnerPendingEvidence: 'OwnerQueue',
  OwnerInProgress: 'OwnerQueue'
};

const INFO_CALLOUT = 'X-VERDICT ships as a generic case engine. Categories, role labels and workflows are per-tenant configuration on the same shell.';

const LIVE_ACTIVITY_LIMIT = 6;
const LIVE_QUEUE_LIMIT = 5;
const OPEN_RUNTIME_STATUSES = new Set(['active', 'warning', 'breached', 'escalated']);

function current_day_label(date = new Date()) {
  return date.toLocaleDateString('en-ZA', { weekday: 'long' }).toUpperCase();
}

function current_date_label(date = new Date()) {
  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).toUpperCase();
}

function time_of_day_greeting(date = new Date()) {
  const hour = date.getHours();

  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function start_of_week(date = new Date()) {
  const result = new Date(date);
  const day = result.getDay() || 7;
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - day + 1);
  return result;
}

function same_day(left, right) {
  return left?.getFullYear() === right?.getFullYear()
    && left?.getMonth() === right?.getMonth()
    && left?.getDate() === right?.getDate();
}

function parse_due_date(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? null : date;
}

function is_open_runtime(runtime = {}) {
  return OPEN_RUNTIME_STATUSES.has(normalize_text(runtime?.status).toLowerCase());
}

function numeric_risk(case_item = {}) {
  return Number(case_item?.risk_score || case_item?.risk || 0) || 0;
}

function normalized_case_text(...values) {
  return values
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

function sla_text(case_item = {}) {
  return normalized_case_text(
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status
  );
}

function is_sla_breached(case_item = {}) {
  const value = sla_text(case_item);
  return value.includes('overdue') || value.includes('breach');
}

function is_sla_warning(case_item = {}) {
  const value = sla_text(case_item);
  return value.includes('left') || value.includes('due') || value.includes('warning');
}

function awaiting_evidence(case_item = {}) {
  return ['evidence', 'document', 'verification', 'pending']
    .some((token) => normalized_case_text(case_item?.status, case_item?.title, case_item?.subtitle).includes(token));
}

function in_review(case_item = {}) {
  return ['review', 'approval', 'decision', 'triage']
    .some((token) => normalized_case_text(case_item?.status, case_item?.title, case_item?.subtitle).includes(token));
}

function needs_decision_today(case_item = {}) {
  const runtime = case_item?.sla_runtime || {};
  const due_date = parse_due_date(runtime?.due_at);

  return is_sla_warning(case_item)
    || (
      Boolean(due_date)
      && is_open_runtime(runtime)
      && same_day(due_date, new Date())
    );
}

function inspector_report(case_item = {}) {
  return ['inspector', 'inspection', 'report', 'field visit']
    .some((token) => normalized_case_text(case_item?.status, case_item?.title, case_item?.subtitle).includes(token));
}

function action_label_for_case(case_item = {}) {
  if (is_sla_breached(case_item)) return 'Decide';
  if (in_review(case_item)) return 'Review';
  if (awaiting_evidence(case_item)) return 'Follow up';
  return 'Open';
}

function action_class_for_case(case_item = {}) {
  return is_sla_breached(case_item)
    ? 'btn-primary-dark'
    : 'btn-light border secondary-color';
}

function chip_badge_class_for_case(case_item = {}) {
  if (is_sla_breached(case_item)) return 'bg-danger-subtle text-danger-emphasis border border-danger-subtle';
  if (is_sla_warning(case_item)) return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
  return 'bg-success-subtle text-success-emphasis border border-success-subtle';
}

function chip_for_case(case_item = {}) {
  if (normalize_text(case_item?.sla)) return case_item.sla;
  if (normalize_text(case_item?.status)) return case_item.status;
  return 'Open case';
}

function queue_meta(case_item = {}) {
  return [
    case_item?.case_code,
    case_item?.domain,
    case_item?.risk_score ? `Risk ${case_item.risk_score}${case_item?.risk_label ? ` (${case_item.risk_label})` : ''}` : '',
    case_item?.sla,
  ].filter(Boolean).join(' - ');
}

function queue_sort_score(case_item = {}) {
  return (is_sla_breached(case_item) ? 1000 : 0)
    + (is_sla_warning(case_item) ? 200 : 0)
    + numeric_risk(case_item);
}

function live_queue_items(cases = []) {
  return cases
    .slice()
    .sort((left, right) => queue_sort_score(right) - queue_sort_score(left))
    .slice(0, LIVE_QUEUE_LIMIT)
    .map((case_item) => ({
      case_code: case_item.case_code,
      title: case_item.title || 'Untitled case',
      meta: queue_meta(case_item),
      chip: chip_for_case(case_item),
      chip_badge_class: chip_badge_class_for_case(case_item),
      action_label: action_label_for_case(case_item),
      action_class: action_class_for_case(case_item),
    }));
}

function matches_current_handler(component, journal = {}) {
  const current_user_id = Number(component.$store?.getters?.currentUserId || 0);
  const journal_user_id = Number(journal?.user_id || journal?.created_by_id || journal?.owner_id || 0);
  if (current_user_id && journal_user_id) {
    return current_user_id === journal_user_id;
  }

  const current_lead_name = normalize_text(component.$store?.getters?.currentLeadName || '').toLowerCase();
  const journal_owner = normalize_text(journal_user_name(journal)).toLowerCase();
  return Boolean(current_lead_name) && current_lead_name === journal_owner;
}

function journal_timestamp(journal = {}) {
  const value = journal?.journal_date || journal?.date_created || journal?.created_at;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function activity_dot_class(journal = {}) {
  if (journal_is_billable(journal)) return 'bg-success';
  return 'bg-warning';
}

function activity_meta(journal = {}) {
  const timestamp = journal_timestamp(journal);
  const label = timestamp
    ? new Date(timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
    : journal_date(journal);
  return `${label} - ${journal_user_name(journal)}`;
}

function activity_text(journal = {}, case_item = {}) {
  return `${journal_activity(journal)} logged on ${case_item?.case_code || 'case'} for ${Number(journal_hours(journal) || 0).toFixed(1)}h.`;
}

function fetch_recent_activity(journal_sets = []) {
  return journal_sets
    .flat()
    .sort((left, right) => journal_timestamp(right.journal) - journal_timestamp(left.journal))
    .slice(0, LIVE_ACTIVITY_LIMIT)
    .map(({ journal, case_item }) => ({
      dot_class: activity_dot_class(journal),
      meta: activity_meta(journal),
      text: activity_text(journal, case_item),
    }));
}

function week_hours_from_journals(journals = []) {
  const week_start = start_of_week(new Date());
  return journals.reduce((total, journal) => {
    const date = new Date(journal?.journal_date || journal?.date_created || journal?.created_at);
    if (Number.isNaN(date.getTime()) || date < week_start) return total;
    return total + Number(journal_hours(journal) || 0);
  }, 0);
}

function live_metric_values(cases = null, week_hours = null) {
  if (!Array.isArray(cases)) {
    return {};
  }

  const warning_cases = cases.filter(is_sla_warning);
  const breached_cases = cases.filter(is_sla_breached);
  const high_risk_cases = cases.filter((case_item) => numeric_risk(case_item) >= 70);
  const review_cases = cases.filter(in_review);
  const evidence_cases = cases.filter(awaiting_evidence);
  const decision_cases = cases.filter(needs_decision_today);
  const inspector_cases = cases.filter(inspector_report);

  return {
    'Decisions due today': { value: String(decision_cases.length), detail: 'Live queue priority' },
    'SLA warning': { value: String(warning_cases.length), detail: '< 48hr to breach' },
    'Awaiting evidence': { value: String(evidence_cases.length), detail: 'Claimant requested' },
    'Hours this week': { value: typeof week_hours === 'number' ? week_hours.toFixed(1) : '', detail: 'Target 35h' },
    'Open cases': { value: String(cases.length), detail: 'Across assigned cases' },
    'In review': { value: String(review_cases.length), detail: 'Ready for next decision' },
    'Pending evidence': { value: String(evidence_cases.length), detail: 'External party requested' },
    'SLA watch': { value: String(warning_cases.length), detail: 'Needs attention this week' },
    'Decision overdue': { value: String(breached_cases.length), detail: breached_cases.length ? 'Escalate or resolve now' : 'None' },
    'Inspector report': { value: String(inspector_cases.length), detail: 'Field findings uploaded' },
    'High risk cases': { value: String(high_risk_cases.length), detail: 'Risk score above 70' },
    'Potential leakage': { value: '', detail: 'Across active escalations' },
    'Approvals due': { value: String(review_cases.length), detail: 'Owner sign-off required' },
    'Policy overrides': { value: String(cases.filter((case_item) => String(case_item?.status || '').toLowerCase().includes('override')).length), detail: 'Exception flow triggered' },
    'Closures pending': { value: String(cases.filter((case_item) => String(case_item?.status || '').toLowerCase().includes('close')).length), detail: 'Final decision still open' },
    'Avg turnaround': { value: '', detail: 'Awaiting live calc' },
  };
}

function merge_live_metrics(base_metrics = [], cases = [], week_hours = 0) {
  const values = live_metric_values(cases, week_hours);
  return base_metrics.map((metric) => ({
    ...metric,
    value: values[metric.label]?.value ?? metric.value,
    detail: values[metric.label]?.detail ?? metric.detail,
  }));
}

function owner_dashboard_summary_segments(cases = null) {
  if (!Array.isArray(cases)) {
    return [{ text: 'Loading live queue summary.' }];
  }

  const values = live_metric_values(cases, null);
  return [
    { text: 'You have ' },
    { text: `${values['Decisions due today']?.value || '0'} cases needing a decision today`, strong: true },
    { text: ', ' },
    { text: `${values['SLA warning']?.value || '0'} SLA warnings`, strong: true },
    { text: ', and ' },
    { text: `${values['Inspector report']?.value || '0'} inspector reports`, strong: true },
    { text: ' waiting in your queue.' },
  ];
}

function build_owner_dashboard_greeting(view, current_user_name, current_view_key, live_cases) {
  const summary_segments = current_view_key === 'OwnerDashboard'
    ? owner_dashboard_summary_segments(live_cases)
    : view.greeting.summary_segments;
  const now = new Date();
  const title = current_view_key === 'OwnerDashboard'
    ? `${time_of_day_greeting(now)}, {name}.`
    : view.greeting.title;

  return {
    ...view.greeting,
    day_label: current_day_label(now),
    date_label: current_date_label(now),
    title: String(title || '').replace('{name}', current_user_name),
    summary_segments,
  };
}

export default {
  name: 'OwnerDashboard',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => ({
    loading_dashboard: false,
    live_cases: null,
    live_week_hours: null,
    live_queue: null,
    live_activity: null,
  }),
  created() {
    this.load_dashboard_data();
  },
  methods: {
    async load_dashboard_data() {
      this.loading_dashboard = true;
      try {
        const cases = await fetch_case_owner_cases(this.project_id);
        this.live_cases = cases;
        this.live_queue = live_queue_items(cases);

        const candidate_cases = this.live_queue
          .map((item) => cases.find((case_item) => case_item.case_code === item.case_code))
          .filter(Boolean);
        const journal_sets = await Promise.all(candidate_cases.map(async (case_item) => {
          try {
            const journals = await fetch_requestor_application_journals(case_item.application_id);
            return journals
              .filter((journal) => matches_current_handler(this, journal))
              .map((journal) => ({ journal, case_item }));
          } catch {
            return [];
          }
        }));
        const journals = journal_sets.flat().map(({ journal }) => journal);

        this.live_activity = fetch_recent_activity(journal_sets);
        this.live_week_hours = week_hours_from_journals(journals);
      } catch {
        this.live_cases = null;
        this.live_queue = null;
        this.live_activity = null;
        this.live_week_hours = null;
      } finally {
        this.loading_dashboard = false;
      }
    },
    go_to_case_detail(case_code) {
      this.$router.push({ name: 'ViewCaseDetails', params: { caseCode: case_code } });
    }
  },
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    current_user_name() {
      return String(this.$store.getters.currentLeadName || 'Case Owner').trim();
    },
    current_view_key() {
      const route_name = OWNER_DASHBOARD_VIEWS[this.$route.name]
        ? this.$route.name
        : OWNER_DASHBOARD_ALIASES[this.$route.name];

      return route_name || 'OwnerDashboard';
    },
    current_view() {
      return OWNER_DASHBOARD_VIEWS[this.current_view_key];
    },
    greeting() {
      return build_owner_dashboard_greeting(
        this.current_view,
        this.current_user_name,
        this.current_view_key,
        this.live_cases
      );
    },
    metrics() {
      return merge_live_metrics(this.current_view.metrics, this.live_cases, this.live_week_hours);
    },
    queue_title() {
      return this.current_view.queue_title;
    },
    queue_subtitle() {
      return this.current_view.queue_subtitle;
    },
    queue_count_label() {
      return Array.isArray(this.live_cases) ? String(this.live_cases.length) : '';
    },
    queue_items() {
      return Array.isArray(this.live_queue) ? this.live_queue : [];
    },
    activity_items() {
      return Array.isArray(this.live_activity) ? this.live_activity : [];
    },
    info_callout() {
      return INFO_CALLOUT;
    },
    page_loading_message() {
      return 'Loading dashboard...';
    },
    page_loading_detail() {
      return 'Pulling your queue, SLA alerts, and recent activity.';
    }
  },
  watch: {
    project_id() {
      this.load_dashboard_data();
    },
    '$route.name'() {
      this.load_dashboard_data();
    }
  }
};
